import type { ReactNode } from 'react';

import { App } from '@capacitor/app';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { useRouter } from 'src/routes/hooks';

import { useAppLockEnabled } from 'src/hooks/use-app-lock';

import { FLAGS } from 'src/global-config-flags';
import { isNativePlatform } from 'src/utils/capacitor';
import { isE2eMode, e2eBiometricShouldFail } from 'src/lib/e2e';

import { useTranslate } from 'src/locales';

import { useAuthContext } from 'src/auth/hooks';
import { signOut } from 'src/auth/context/firebase/action';

import { Iconify } from 'src/components/iconify';

import {
  type BiometricReason,
  type BiometricCheckResult,
  type BiometricFailureCause,
  BIOMETRIC_DEBOUNCE_MS,
  SESSION_LOCK_IDLE_MS,
  DEFAULT_HIGH_VALUE_THRESHOLD_INR,
} from './biometric-gate.types';

// ----------------------------------------------------------------------

const LAST_AUTH_KEY = 'artham.lastBiometricAt';

function readLastAuthAt(): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(LAST_AUTH_KEY);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) ? n : 0;
}

function writeLastAuthAt(ts: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LAST_AUTH_KEY, String(ts));
}

async function callPlugin(reason: BiometricReason): Promise<BiometricCheckResult> {
  // E2E browser harness: the native biometric plugin cannot run in a browser,
  // so resolve deterministically. `artham.e2e.biometric='fail'` drives the
  // decline path. Never reached in a real Capacitor build.
  if (isE2eMode()) {
    if (e2eBiometricShouldFail()) {
      return { ok: false, reason, cause: 'cancelled' };
    }
    writeLastAuthAt(Date.now());
    return { ok: true, reason, viaPlugin: true };
  }
  // Desktop/web has no native biometric bridge — `checkBiometry()` never
  // resolves there, which would hang the session gate on "Verifying…" forever.
  // Treat web as a no-op so the gate unlocks and high-value flows proceed.
  if (!isNativePlatform()) {
    return { ok: true, reason, viaPlugin: false };
  }
  try {
    const mod = await import('@aparajita/capacitor-biometric-auth');
    const { BiometricAuth } = mod;
    const info = await BiometricAuth.checkBiometry();
    // Truly unlockable only when there is NO enrolled biometric AND no device
    // passcode/PIN to fall back to — i.e. the device has no security boundary
    // to enforce. Otherwise `allowDeviceCredential` lets the OS present the
    // passcode sheet, so an un-enrolled user is still challenged rather than
    // silently waved through.
    if (!info.isAvailable && !info.deviceIsSecure) {
      return { ok: false, reason, cause: 'unavailable' };
    }
    const reasonText =
      reason === 'session-lock' ? 'Use biometric to continue' : 'Authorize this transaction';
    await BiometricAuth.authenticate({
      reason: reasonText,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Use passcode',
      androidTitle: 'Unlock Artham',
    });
    writeLastAuthAt(Date.now());
    return { ok: true, reason, viaPlugin: true };
  } catch (err: any) {
    return { ok: false, reason, cause: mapBiometryError(err) };
  }
}

/**
 * Map a plugin `BiometryError` to a failure cause. Compares against the actual
 * `BiometryErrorType` string values (`biometryNotEnrolled`, `biometryLockout`,
 * …) — the previous substring heuristics (`'not enrolled'`, `'unavailable'`)
 * never matched those codes and mis-classified everything as `unknown`.
 */
function mapBiometryError(err: any): BiometricFailureCause {
  const code = String(err?.code ?? '');
  switch (code) {
    case 'userCancel':
    case 'appCancel':
    case 'systemCancel':
      return 'cancelled';
    case 'biometryLockout':
      return 'locked-out';
    case 'biometryNotAvailable':
    case 'biometryNotEnrolled':
    case 'passcodeNotSet':
      return 'unavailable';
    default:
      return 'unknown';
  }
}

/**
 * Imperative biometric check used at high-value transaction submit.
 *
 * Resolves to `{ ok: true }` when the user authorized OR the gate did not need
 * to fire (flag off / amount below threshold / inside debounce window).
 * Callers should treat `{ ok: false }` as a hard cancel and not proceed.
 */
export async function requireBiometric(
  reason: BiometricReason,
  opts: { amount?: number; threshold?: number } = {}
): Promise<BiometricCheckResult> {
  if (!FLAGS.BIOMETRIC_GATE_ENABLED) {
    return { ok: true, reason, viaPlugin: false };
  }
  if (reason === 'high-value-txn') {
    const threshold = opts.threshold ?? DEFAULT_HIGH_VALUE_THRESHOLD_INR;
    const amount = opts.amount ?? 0;
    if (amount < threshold) {
      return { ok: true, reason, viaPlugin: false };
    }
  }
  const sinceLast = Date.now() - readLastAuthAt();
  if (sinceLast < BIOMETRIC_DEBOUNCE_MS) {
    return { ok: true, reason, viaPlugin: false };
  }
  return callPlugin(reason);
}

/**
 * One-shot biometric challenge used when the user turns the app lock on, so we
 * only persist the preference after confirming the device can actually satisfy
 * it. A success also stamps `lastBiometricAt`, which keeps the gate from
 * immediately re-prompting.
 */
export function authenticateForAppLock(): Promise<BiometricCheckResult> {
  return callPlugin('session-lock');
}

// ----------------------------------------------------------------------

type SessionGateProps = {
  children: ReactNode;
};

/**
 * Biometric session-lock. Prompts at app open and re-arms on resume once the
 * app has been backgrounded past {@link SESSION_LOCK_IDLE_MS}. Offers a
 * device-passcode fallback (via the plugin) and a sign-out escape hatch so a
 * user can never be permanently bricked behind a failing biometric.
 *
 * Opt-in: no-op unless the user turned the app lock on (see `use-app-lock`),
 * and off entirely while {@link FLAGS.BIOMETRIC_GATE_ENABLED} is off.
 *
 * While locked the children stay mounted behind the overlay — unmounting them
 * threw away in-progress form state and reset the visible route on every
 * app-switch.
 */
export function BiometricSessionGate({ children }: SessionGateProps) {
  const { t } = useTranslate('arthamShared');
  const router = useRouter();
  const { checkUserSession } = useAuthContext();
  const lockPreferred = useAppLockEnabled();
  // Web/desktop has no native biometric bridge — never gate there, so the
  // guard can't render (and can't hang on "Verifying…"). Native only.
  const enabled = FLAGS.BIOMETRIC_GATE_ENABLED && lockPreferred && isNativePlatform();
  const [unlocked, setUnlocked] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<BiometricFailureCause | null>(null);
  const locked = enabled && !unlocked;

  // Refs mirror state for the appStateChange callback (registered once) and to
  // guard against overlapping auth attempts without re-subscribing.
  const pendingRef = useRef(false);
  const unlockedRef = useRef(unlocked);
  const backgroundedAtRef = useRef<number | null>(null);
  useEffect(() => {
    unlockedRef.current = unlocked;
  }, [unlocked]);

  const attempt = useCallback(async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    const result = await callPlugin('session-lock');
    pendingRef.current = false;
    setPending(false);
    if (result.ok) {
      setUnlocked(true);
    } else {
      setError(result.cause);
      // Only bypass when the device has no biometrics AND no passcode at all —
      // there is no boundary to enforce. Every other failure keeps the gate up.
      if (result.cause === 'unavailable') {
        setUnlocked(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled || unlocked) return;
    const lastAt = readLastAuthAt();
    if (Date.now() - lastAt < SESSION_LOCK_IDLE_MS) {
      setUnlocked(true);
      return;
    }
    attempt();
  }, [enabled, unlocked, attempt]);

  // Re-arm the lock across background/foreground. The mount effect alone never
  // re-locks a live process, so the app stayed open indefinitely after resume.
  // We record when the app is backgrounded (only while unlocked and idle, so
  // the biometric sheet's own backgrounding is ignored) and re-lock on return
  // once the away time exceeds the grace window.
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let handle: { remove: () => void } | undefined;
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        if (unlockedRef.current && !pendingRef.current) {
          backgroundedAtRef.current = Date.now();
        }
        return;
      }
      const bgAt = backgroundedAtRef.current;
      backgroundedAtRef.current = null;
      if (bgAt != null && Date.now() - bgAt >= SESSION_LOCK_IDLE_MS) {
        setError(null);
        setUnlocked(false);
      }
    }).then((h) => {
      if (active) handle = h;
      else h.remove();
    });
    return () => {
      active = false;
      handle?.remove();
    };
  }, [enabled]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      await checkUserSession?.();
      router.refresh();
    } catch {
      // signOut() clears storage even on failure; a refresh drops to sign-in.
      router.refresh();
    }
  }, [checkUserSession, router]);

  return (
    <>
      {children}
      {locked && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            px: 3,
          }}
        >
          <Stack spacing={3} sx={{ maxWidth: 360, textAlign: 'center' }}>
            <Iconify
              icon="solar:shield-keyhole-bold-duotone"
              width={64}
              sx={{ mx: 'auto', color: 'primary.main' }}
            />
            <Typography variant="h5">{t('biometric.welcomeBack')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('biometric.continuePrompt')}
            </Typography>
            {error && (
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                {error === 'cancelled'
                  ? t('biometric.authCancelled')
                  : error === 'locked-out'
                    ? t('biometric.lockedOut')
                    : t('biometric.verifyError')}
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={attempt}
              disabled={pending}
              startIcon={pending ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {pending ? t('biometric.verifying') : t('biometric.unlock')}
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="small"
              onClick={handleSignOut}
              disabled={pending}
            >
              {t('biometric.signOut')}
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

export type BiometricThresholdContext = {
  threshold: number;
  source: 'metadata' | 'default';
};

/**
 * Resolves the high-value threshold from `users.metadata.biometricThreshold`
 * with a ₹10,000 fallback. UI surfaces can render the source in tooltips.
 */
export function useBiometricThreshold(): BiometricThresholdContext {
  const { user } = useAuthContext();
  const fromMetadata = Number((user?.metadata as any)?.biometricThreshold);
  if (Number.isFinite(fromMetadata) && fromMetadata > 0) {
    return { threshold: fromMetadata, source: 'metadata' };
  }
  return { threshold: DEFAULT_HIGH_VALUE_THRESHOLD_INR, source: 'default' };
}
